// @flow

import { CreditCard } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Hidden } from 'reshaped';

import { selectCartUUID as selectCartId } from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';

import { Badge, Button } from '@gandi/reshaped';

import { useTranslator } from '@gandi/translate';

import { doCommitCart } from 'src/Overview/modules/actions';
import { selectIsCheckoutButtonDisabled } from 'src/Overview/modules/selectors';
import { useAppSelector } from 'src/hooks/useAppSelector';

type Params = {
  cartId: string,
  orderId?: string,
};

type Props = {
  price: string,
};

export function CheckoutButton({ price }: Props): React.MixedElement {
  const translator = useTranslator();
  const { __ } = translator;
  const dispatch = useDispatch();

  const { orderId } = useParams<Params>();
  const disabled = useAppSelector(selectIsCheckoutButtonDisabled);
  const cartId = useAppSelector(selectCartId);
  const navigate = useNavigate();

  const handleClick = async (evt: SyntheticMouseEvent<>) => {
    evt.preventDefault();

    try {
      const response = await dispatch(doCommitCart());
      navigate(`/cart/${cartId}/checkout/${response.data.session_id}`);
    } catch (error) {
      const title = error?.response?.data.message || __('Cart checkout error');
      const message = error.errors
        ? error.errors.map(({ description }) => description)
        : __('An error occurred when committing your cart.');
      dispatch(
        notifyDanger({
          title,
          message,
        }),
      );
    }
  };

  return (
    <Button
      key="cart-checkout"
      size="xlarge"
      color="primary"
      fullWidth
      to={orderId ? `/cart/${cartId}/checkout/${orderId}` : undefined}
      disabled={!orderId ? disabled : undefined}
      // $FlowFixMe[incompatible-type]
      onClick={!orderId && !disabled ? handleClick : undefined}
      icon={CreditCard}
    >
      {__('<for-translators-use>Proceed to payment</for-translators-use>Pay')}

      <Hidden hide={{ s: false, m: true }}>
        <Badge size="large" rounded>
          {price}
        </Badge>
      </Hidden>
    </Button>
  );
}
